import { useState, useEffect, useCallback, useRef } from 'react';
import * as THREE from 'three';

export interface ViewState3D {
  // Camera state
  cameraPosition: { x: number; y: number; z: number };
  cameraTarget: { x: number; y: number; z: number };
  cameraZoom: number;
  
  // Selection state
  selectedNodeId?: string;
  selectedNodes: string[];
  
  // View preferences
  showLabels: boolean;
  showMetrics: boolean;
  enablePhysics: boolean;
  
  // Timestamp for cache invalidation
  timestamp: number;
}

const DEFAULT_VIEW_STATE: ViewState3D = {
  cameraPosition: { x: 0, y: 0, z: 100 },
  cameraTarget: { x: 0, y: 0, z: 0 },
  cameraZoom: 1,
  selectedNodeId: undefined,
  selectedNodes: [],
  showLabels: true,
  showMetrics: true,
  enablePhysics: true,
  timestamp: Date.now(),
};

const STORAGE_KEY = 'socketcloud-3d-view-state';
const STATE_VERSION = '1.0';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Custom hook for managing 3D view state persistence
 * Automatically saves and restores camera position, selections, and preferences
 */
export function use3DViewState() {
  const [viewState, setViewState] = useState<ViewState3D>(DEFAULT_VIEW_STATE);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const isInitializedRef = useRef(false);

  // Load saved state on mount
  useEffect(() => {
    const loadSavedState = () => {
      try {
        const savedStateJson = localStorage.getItem(STORAGE_KEY);
        if (savedStateJson) {
          const savedData = JSON.parse(savedStateJson);
          
          // Check version and age
          if (savedData.version === STATE_VERSION && 
              savedData.state && 
              (Date.now() - savedData.state.timestamp) < MAX_AGE) {
            
            setViewState(savedData.state);
            console.log('3D View: Restored saved view state', savedData.state);
          } else {
            console.log('3D View: Saved state expired or incompatible, using defaults');
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.warn('3D View: Failed to load saved state:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
      
      isInitializedRef.current = true;
    };

    loadSavedState();
  }, []);

  // Save state to localStorage (debounced)
  const saveState = useCallback((newState: ViewState3D) => {
    if (!isInitializedRef.current) {
return;
}

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce saving to avoid excessive localStorage writes
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const stateToSave = {
          version: STATE_VERSION,
          state: {
            ...newState,
            timestamp: Date.now(),
          },
        };
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
        console.log('3D View: Saved view state', stateToSave.state);
      } catch (error) {
        console.warn('3D View: Failed to save state:', error);
      }
    }, 500); // 500ms debounce
  }, []);

  // Update camera state
  const updateCameraState = useCallback((camera: THREE.Camera, target?: THREE.Vector3) => {
    const newState: ViewState3D = {
      ...viewState,
      cameraPosition: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      },
      cameraTarget: target ? {
        x: target.x,
        y: target.y,
        z: target.z,
      } : viewState.cameraTarget,
      cameraZoom: camera instanceof THREE.PerspectiveCamera ? camera.zoom : 1,
    };

    setViewState(newState);
    saveState(newState);
  }, [viewState, saveState]);

  // Update selection state
  const updateSelection = useCallback((selectedNodeId?: string, selectedNodes?: string[]) => {
    const newState: ViewState3D = {
      ...viewState,
      selectedNodeId,
      selectedNodes: selectedNodes || viewState.selectedNodes,
    };

    setViewState(newState);
    saveState(newState);
  }, [viewState, saveState]);

  // Update view preferences
  const updatePreferences = useCallback((preferences: Partial<Pick<ViewState3D, 'showLabels' | 'showMetrics' | 'enablePhysics'>>) => {
    const newState: ViewState3D = {
      ...viewState,
      ...preferences,
    };

    setViewState(newState);
    saveState(newState);
  }, [viewState, saveState]);

  // Apply saved state to camera and controls
  const applyCameraState = useCallback((camera: THREE.Camera, controls: any) => {
    if (!isInitializedRef.current) {
return;
}

    const { cameraPosition, cameraTarget, cameraZoom } = viewState;
    
    // Set camera position
    camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    
    // Set camera zoom
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.zoom = cameraZoom;
      camera.updateProjectionMatrix();
    }
    
    // Set controls target if available
    if (controls && controls.target) {
      controls.target.set(cameraTarget.x, cameraTarget.y, cameraTarget.z);
      controls.update();
    }

    console.log('3D View: Applied saved camera state', { cameraPosition, cameraTarget, cameraZoom });
  }, [viewState]);

  // Clear saved state
  const clearState = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setViewState(DEFAULT_VIEW_STATE);
    console.log('3D View: Cleared saved state');
  }, []);

  // Save state immediately (for manual saves)
  const saveStateImmediate = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    try {
      const stateToSave = {
        version: STATE_VERSION,
        state: {
          ...viewState,
          timestamp: Date.now(),
        },
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      console.log('3D View: Immediately saved view state');
    } catch (error) {
      console.warn('3D View: Failed to save state immediately:', error);
    }
  }, [viewState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Save state immediately on unmount
      if (isInitializedRef.current) {
        try {
          const stateToSave = {
            version: STATE_VERSION,
            state: {
              ...viewState,
              timestamp: Date.now(),
            },
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
        } catch (error) {
          console.warn('3D View: Failed to save state on unmount:', error);
        }
      }
    };
  }, [viewState]);

  return {
    viewState,
    isInitialized: isInitializedRef.current,
    updateCameraState,
    updateSelection,
    updatePreferences,
    applyCameraState,
    clearState,
    saveStateImmediate,
  };
}

export default use3DViewState;